import { useEffect, useState } from 'react';
import {
  Modal,
  Box,
  Button,
  FormField,
  Input,
  Multiselect,
  Select,
  SpaceBetween,
  Spinner,
} from '@amzn/awsui-components-console';
import {
  CreateGroupCommand,
  LaunchTask,
  ListLaunchTasksCommand,
  ListLaunchTasksCommandOutput,
  ListVenuesCommand,
  ListVenuesCommandOutput,
  VenueInfoItem,
  WorkStatus,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { useAuthState } from '../authentication/context';
import { callApi } from '../client';
import { AsyncErrorBar } from '../components/AsyncErrorBar';
import { paginateAll, PAGE_SIZE } from '../utils/pagination';
import { canonicalOrRawVenue, compositeVenueKey } from '../utils/venueDisplay';
import { useLatestRequest } from '../hooks/useLatestRequest';

interface CreateGroupModalProps {
  visible: boolean;
  onDismiss: () => void;
  onSuccess: (groupId: string) => void;
}

const GROUP_NAME_PATTERN = /^[\x20-\x7E]{1,128}$/;
const EXCLUDED_STATUSES: string[] = [
  WorkStatus.GROUPED,
  WorkStatus.DELETED,
  WorkStatus.RESOLVED,
  WorkStatus.SHIPPED,
];

export const CreateGroupModal = ({
  visible,
  onDismiss,
  onSuccess,
}: CreateGroupModalProps) => {
  const [groupName, setGroupName] = useState('');
  const [selectedVenue, setSelectedVenue] = useState<any>(null);
  const [conference, setConference] = useState<string | null>(null);
  const [conferenceError, setConferenceError] = useState(false);
  const [venues, setVenues] = useState<any[]>([]);
  const [venuesLoading, setVenuesLoading] = useState(false);
  const [tasks, setTasks] = useState<LaunchTask[]>([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { token } = useAuthState();
  // Latest-request-wins guard: rapidly switching venues fires overlapping task loads; only the most
  // recent one may commit, so a slow earlier load can't overwrite a newer venue's tasks (CR-301380769).
  const tasksRequest = useLatestRequest();

  const resetForm = () => {
    setGroupName('');
    setSelectedVenue(null);
    setSelectedTasks([]);
    setConference(null);
    setConferenceError(false);
    setError(null);
    setTasks([]);
  };

  const handleCancel = () => {
    resetForm();
    onDismiss();
  };

  // Load venues on mount
  useEffect(() => {
    if (visible && token) {
      void loadVenues();
    }
  }, [visible, token]);

  // venue-change trigger
  useEffect(() => {
    if (selectedVenue?.value && token) {
      setSelectedTasks([]);
      // Conference comes directly from the selected venue option — no longer inferred from the
      // first matching task (which was arbitrary on a cross-conference venue-name collision).
      setConference(selectedVenue.conferenceName ?? null);
      setConferenceError(!selectedVenue.conferenceName);
      void loadTasksForVenue(
        selectedVenue.conferenceName,
        selectedVenue.venueName
      );
    }
  }, [selectedVenue]);

  const loadVenues = async () => {
    if (!token) return;
    setVenuesLoading(true);
    try {
      const response: ListVenuesCommandOutput = await callApi(
        new ListVenuesCommand({}),
        token
      );
      if (response.$metadata.httpStatusCode === 200) {
        const venueOptions = (response.venues ?? []).map(
          (v: VenueInfoItem) => ({
            // Display the coordinator's canonical name when set (and non-blank), else the raw venue
            // (display-only; routing/identity stay on venueName). tcx-core-platforms-903 D2.
            label: `${canonicalOrRawVenue(v.canonicalVenueName, v.venue)} (${
              v.conference
            })`,
            // Composite value so two conferences with a same-named venue don't collide on the Select's
            // value key; built via the shared helper so the delimiter can't drift (F1 / CR-301380769).
            value: compositeVenueKey(v.conference, v.venue),
            description: v.conference,
            conferenceName: v.conference,
            venueName: v.venue,
          })
        );
        setVenues(venueOptions);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load venues');
    } finally {
      setVenuesLoading(false);
    }
  };

  const loadTasksForVenue = async (conference: string, venue: string) => {
    if (!token) return;
    try {
      await tasksRequest.run(
        setTasksLoading,
        () =>
          // Scope server-side to the selected (conference, venue) and page through every result rather
          // than the first ≤1000 (tcx-core-platforms-903 §8 F7 / F1).
          paginateAll<LaunchTask>((nextToken) =>
            callApi(
              new ListLaunchTasksCommand({
                conference,
                venue,
                pageSize: PAGE_SIZE,
                nextToken,
              }),
              token
            ).then((response: ListLaunchTasksCommandOutput) => ({
              items: response.tasks ?? [],
              nextToken: response.nextToken,
              statusCode: response.$metadata.httpStatusCode ?? 0,
            }))
          ),
        ({ items, ok, truncated }) => {
          // Only commit the list if every page succeeded — a non-200 mid-pagination must surface an
          // error rather than silently showing a partial/empty task list (tcx-core-platforms-903,
          // finding on CR-301380769). A truncated walk (more pages than the cap allows) is treated as
          // an error too. The superseded-load drop and loading-flag reset are owned by run()
          // (CR-301380769 rev5).
          if (!ok || truncated) {
            setError('Failed to load tasks');
            return;
          }

          // Defensive: the server already scopes to (conference, venue); keep the client filter so a
          // same-named venue in another conference can never leak in.
          const venueTasks = items.filter(
            (t) => t.venue === venue && t.conference === conference
          );
          const validTasks = venueTasks.filter(
            (t) => !EXCLUDED_STATUSES.includes(t.workStatus)
          );
          setTasks(validTasks);
        }
      );
    } catch (err: any) {
      setError(err?.message || 'Failed to load tasks');
    }
  };

  const taskOptions = tasks.map((t) => ({
    label: t.name || t.launchId,
    value: t.launchId,
    description: t.launchId,
  }));

  const isValid =
    groupName.trim().length > 0 &&
    GROUP_NAME_PATTERN.test(groupName) &&
    selectedVenue &&
    conference &&
    !conferenceError &&
    selectedTasks.length > 0;

  const handleSubmit = async () => {
    if (!token || !isValid) return;
    setLoading(true);
    setError(null);

    const memberTaskIds = selectedTasks.map((o: any) => o.value);

    try {
      const response = await callApi(
        new CreateGroupCommand({
          groupName: groupName.trim(),
          conference: conference,
          venue: selectedVenue.venueName,
          memberTaskIds,
        }),
        token
      );
      if (response.group) {
        onSuccess(response.group.groupId);
      }
    } catch (err: any) {
      if (err?.statusCode === 409) {
        setError(
          'A group with this name already exists in this venue. Please choose a different name.'
        );
      } else {
        setError(err?.message || String(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={handleCancel}
      header='Create group'
      footer={
        <Box float='right'>
          <SpaceBetween direction='horizontal' size='xs'>
            <Button variant='link' onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              variant='primary'
              onClick={handleSubmit}
              loading={loading}
              disabled={!isValid}
              data-test-id='confirm-create-group-button'
            >
              Create group
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size='m'>
        <AsyncErrorBar error={error} header='Create Group Failed' />
        <FormField
          label='Group name'
          constraintText='1–128 printable ASCII characters'
        >
          <Input
            value={groupName}
            onChange={({ detail }) => setGroupName(detail.value)}
            data-test-id='group-name-input'
          />
        </FormField>
        <FormField label='Venue'>
          <Select
            selectedOption={selectedVenue}
            onChange={({ detail }) => setSelectedVenue(detail.selectedOption)}
            options={venues}
            placeholder='Select a venue'
            loadingText='Loading venues'
            statusType={venuesLoading ? 'loading' : 'finished'}
            filteringType='auto'
            data-test-id='venue-select'
          />
        </FormField>
        <FormField label='Conference'>
          <Input
            value={
              conferenceError
                ? 'Could not determine conference from venue selection'
                : conference ?? 'Select a venue first'
            }
            readOnly
            disabled={!conference}
          />
        </FormField>
        <FormField
          label='Initial tasks'
          constraintText='At least 1 task required'
        >
          {tasksLoading ? (
            <Spinner />
          ) : (
            <Multiselect
              selectedOptions={selectedTasks}
              onChange={({ detail }) =>
                setSelectedTasks([...detail.selectedOptions])
              }
              options={taskOptions}
              placeholder={
                selectedVenue
                  ? 'Select tasks'
                  : 'Select a venue to load available tasks'
              }
              disabled={!selectedVenue || tasksLoading}
              filteringType='auto'
              data-test-id='initial-tasks-multiselect'
            />
          )}
        </FormField>
      </SpaceBetween>
    </Modal>
  );
};
